/**
 * Module-level store for the post-payment redirect URL.
 *
 * When a merchant navigates to webregister from an external page (e.g. monocle
 * customers or service-orders) to process a payment, the
 * `onPaymentCompletedRedirectUrl` query param is stored here on startup and
 * consumed once the payment flow completes to redirect back to the originating
 * page.
 *
 * A module variable is used instead of Redux or sessionStorage because this is
 * a one-shot transient navigation concern — it lives only for the duration of
 * the current page session and has no value persisting across reloads.
 *
 * Callers are responsible for gating access behind the appropriate feature flag.
 * This module only enforces open-redirect protection.
 */

let returnUrl: string | null = null

export const setPostPaymentRedirectUrl = (url: string): void => {
  // Prevent open-redirect: only allow relative paths (no protocol/external host).
  const isSafeRelativePath = url.startsWith('/') && !url.startsWith('//')
  if (!isSafeRelativePath) {
    return
  }
  returnUrl = url
}

/**
 * Returns the stored return URL without clearing it.
 * Use this to check whether the current session has a post-payment redirect
 * without consuming the URL (e.g. to conditionally change UI labels).
 */
export const peekPostPaymentRedirectUrl = (): string | null => returnUrl

/**
 * Returns the stored return URL and clears it, so it can only be consumed once.
 */
export const getAndClearPostPaymentRedirectUrl = (): string | null => {
  const url = returnUrl
  returnUrl = null
  return url
}

/**
 * Clears the stored return URL without returning it.
 * Use in error paths to prevent a stale URL from affecting subsequent flows.
 */
export const clearPostPaymentRedirectUrl = (): void => {
  returnUrl = null
}
